import styled from "styled-components/macro";
import Link from "ui/Link";
import { theme } from "ui/theme";
import PolicyLayout, {
  $PageTitle,
  $TextContainer,
  $OverviewText,
  $SectionTitle,
  $SubsectionTitle,
  $Paragraph,
  $UnorderedList,
  $ListItem,
  $SummaryBox,
  $SummaryBoxTitle,
  $ExternalLink,
  $PrivacyPolicyLink,
  $CodeOfConductLink,
  $SecurityWhitepaperLink,
  $DMCANotificationGuidelinesLink,
  $PrivacyAtAlongEmailLink,
  $SecurityAtAlongEmailLink,
  $LegalAtAlongEmailLink,
} from "templates/PolicyLayout";
import { EventName } from "utils/analytics/analyticsConstants";

import PaperAndPencil from "images/paper-and-pencil.png";
import PaperAndPencil2x from "images/paper-and-pencil@2x.png";
import CircleMint from "images/circle-mint.png";

const $PageTitleTextDecorator = styled.span`
  position: relative;
  display: inline-block;

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: -50%;
    left: -5%;
    width: 110%;
    height: 160%;
    background-image: url(${CircleMint});
    background-repeat: no-repeat;
    background-position: bottom center;
    background-size: 100%;
    z-index: ${theme.zIndexBelow};

    @media screen and (${theme.BreakpointTabletDown}) {
      display: none;
    }
  }
`;

const $SectionTitleBold = styled($SectionTitle)`
  font-weight: ${theme.FontWeightBold};
`;

const PageTitle = () => {
  return (
    <$PageTitle>
      <$PageTitleTextDecorator>User Agreement</$PageTitleTextDecorator>
    </$PageTitle>
  );
};

export default function UserAgreement() {
  return (
    <PolicyLayout
      pageTitle={<PageTitle />}
      lastUpdatedDate="December 19, 2023"
      src={PaperAndPencil}
      srcSet={PaperAndPencil2x}
      alt="a graphic illustration of a pen writing on paper"
    >
      <$TextContainer>
        <$OverviewText size="h3">
          <$Paragraph>
            Please be advised that this agreement contains an arbitration
            provision in Section 6 below that affects your rights under this
            agreement. Except for certain types of disputes mentioned in that
            provision, you and Gradient Learning agree that (1) disputes between
            us will be resolved by individual binding arbitration, and (2) you
            and Gradient Learning waive any right to participate in a
            class-action lawsuit, class-wide arbitration, or any other
            representative action.
          </$Paragraph>
          <$Paragraph>
            We have done our best to write this User Agreement in simple and
            clear terms. In order to facilitate your understanding of the User
            Agreement we have added notes that provide short summaries of the
            legal language in plain English.
          </$Paragraph>
        </$OverviewText>

        <$SectionTitle size="h1">Introduction</$SectionTitle>

        <$Paragraph>
          Along is a free, web-based, digital teacher-student connection builder
          - helping teachers to better understand their students and increase
          student engagement in the classroom. The Services (defined below)
          enabled by Along have been developed to facilitate meaningful
          communication (in the form of multiple choice questions, asynchronous
          text, video and/or other supported methods of communication) between
          teachers and students through research-informed reflection questions
          and resources.
        </$Paragraph>
        <$Paragraph>
          Gradient Learning (formerly T.L.P. Education) (“
          <strong>Gradient Learning</strong>”, “<strong>we</strong>”, “
          <strong>us</strong>” and “<strong>our</strong>”), a California
          nonprofit public benefit organization and 501(c)(3) nonprofit
          organization, is dedicated to helping all students feel encouraged and
          inspired. With support from the Chan Zuckerberg Initiative (“
          <strong>CZI</strong>”), Gradient Learning offers Along as a free
          service to teachers and students. For clarity, this User Agreement
          does not apply to any other product or service offered by Gradient
          Learning, such as Summit Learning, which is offered under separate
          terms and conditions.
        </$Paragraph>
        <$Paragraph>
          <strong>
            Use of the Services (defined below) is governed by this User
            Agreement (the “User Agreement”) including any exhibits, our{" "}
            <$PrivacyPolicyLink /> (the “Privacy Policy”), which identifies the
            personal information that we collect and how we use and share it,
            and our <$CodeOfConductLink />, which has rules that helps keep
            Along a safe space for teachers and students.
          </strong>
        </$Paragraph>

        <$SectionTitle size="h1">1. Contract and Services</$SectionTitle>

        <$SubsectionTitle size="h3">1.1 Contract</$SubsectionTitle>

        <$Paragraph>
          When you use our Services you agree to all of the terms in this User
          Agreement, any Addenda to this User Agreement, and our&nbsp;
          <$PrivacyPolicyLink />, which are incorporated into this User
          Agreement. This User Agreement applies to (a) teachers who register
          for the Services (“
          <strong>Registered Teachers</strong>”) and students invited to use the
          Services by a Registered Teacher (“
          <strong>Student Users</strong>”) (together, “<strong>Users</strong>
          ”), and (b) school administrators and school district officials of
          Schools (“<strong>School Administrators</strong>”) whose teachers and
          students are Users.
        </$Paragraph>
        <$Paragraph>
          You agree that by registering for, accessing, or using our Services as
          a Registered Teacher or School Administrator, you are agreeing to
          enter into a legally binding contract, on behalf of, and as an
          authorized agent of, the educational institution through which you
          conduct learning activities (“<strong>School</strong>”), with Gradient
          Learning, and warrant that you have authority to enter into a contract
          on behalf of your School and bind it to these terms and conditions, in
          which case the terms “you”, “your” or “User” shall refer to your
          School.
        </$Paragraph>
        <$Paragraph>
          If you do not agree to the terms in this User Agreement and our&nbsp;
          <$PrivacyPolicyLink />, or you do not have authority to agree on
          behalf of the School, do not register for, access, or otherwise use
          any of our Services.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            This User Agreement applies to teachers and their students who use
            Along, as well as to school administrators of the schools where
            those teachers work. By using Along, you agree to the terms
            contained in this User Agreement, its addenda, and our&nbsp;
            <$PrivacyPolicyLink />. You confirm that you have the authority to
            agree to these terms on behalf of your school and to enter your
            School into a contract with Gradient Learning.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">1.2 Services</$SubsectionTitle>

        <$Paragraph>
          This User Agreement applies to Along, including its website, features,
          and related services accessed through Along (collectively “
          <strong>Services</strong>”); our provision of the Services; and your
          access to and use of them.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            This User Agreement applies to all parts of our service.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">1.3 Change</$SubsectionTitle>

        <$Paragraph>
          We may modify this User Agreement or our&nbsp;
          <$PrivacyPolicyLink />
          &nbsp;from time to time. Except as otherwise provided in this User
          Agreement, if we make material changes to this User Agreement,
          including changes that impair your rights, we will provide you at
          least 30 days prior notice pursuant to this Section 1.3. We will seek
          to provide you notice through our Services, or by other means, to
          provide you the opportunity to review the changes before they become
          effective. We agree that changes cannot be retroactive. The notice
          will indicate which sections contain material modifications and what
          choices you may have. Your continued use of our Services after we
          publish or send a notice about our changes to these terms means that
          you are consenting to the updated terms following their “effective
          date.” If you object to any changes, you must stop using or accessing
          the Services. If we make any changes to this User Agreement or&nbsp;
          <$PrivacyPolicyLink />, you can request a copy of the prior versions
          by contacting us at&nbsp;
          <$PrivacyAtAlongEmailLink />.
        </$Paragraph>

        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We may make changes to this User Agreement or our&nbsp;
            <$PrivacyPolicyLink />
            &nbsp;as we improve or add more features to Along. When we make
            material changes to these agreements we will notify you, and your
            continued use will be under the new terms.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">2.Obligations of Users</$SectionTitle>

        <$SubsectionTitle size="h3">2.1 Services Eligibility</$SubsectionTitle>

        <$Paragraph>
          Only currently enrolled students, teachers of currently enrolled
          students, and School Administrators are allowed to access the
          Services. Creating an account with false information is a violation of
          our terms. The right to access and use the Services is not granted in
          jurisdictions outside the United States.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Only school personnel and students within the United States are
            allowed to register for Along.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          2.2 Access to the Services
        </$SubsectionTitle>

        <$Paragraph>
          You agree to limit access to the Services to yourself and to any
          Student Users you invite. You may establish and manage accounts for
          Student Users (“<strong>Student Accounts</strong>”) and invite Student
          Users to access the Services using Student Accounts. You are
          responsible for the proper use of the Services by any Student Users
          you invite, and you agree to protect the personal information of such
          students according to the terms of the Data Privacy Addendum.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Teachers are responsible for the proper use of Along by students
            they invite to Along. This includes protecting the personal
            information of students as described in the Data Privacy Addendum
            below.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          2.3 Accounts and Passwords
        </$SubsectionTitle>

        <$Paragraph>
          You agree to: (1) properly log out from your account at the end of
          each session; (2) not transfer or share your account; and (3) follow
          the law and the terms of this User Agreement. You are responsible for
          anything that happens through your account unless you report misuse
          through the contact information in Section 9 below. If a password is
          required to use the Services, you also agree to: (4) choose a strong
          and secure password and (5) keep your password secure and
          confidential.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            It’s important to keep your account secure as you are responsible
            for anything that happens through your account. By using Along, you
            agree to protect the security of your account by taking security
            measures such as logging out of your account after each session, not
            transferring or sharing your account, and following the law and the
            terms of this User Agreement.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">2.4 No Payment</$SubsectionTitle>

        <$Paragraph>
          The Services are provided at no charge and there are no features
          related to purchasing goods and services included in the Services.
          Note: the Along Teacher Library includes content from nonprofit
          organizations which may link to their third-party websites and
          services. We never sell or rent User personal information, including
          for targeted advertising. Please see our&nbsp;
          <$PrivacyPolicyLink />
          &nbsp;for more information about the data we collect and how we use,
          share, and protect it.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Along is free to use. We never rent or sell your personal
            information, and we will not try to sell you anything through our
            service.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">2.5 Notices and Messages</$SubsectionTitle>

        <$Paragraph>
          You agree that we can provide notices and messages to you and your
          School related to the Services in the following ways: (1) within the
          Services, or (2) sent to the contact information you provided us
          (e.g., email, mobile number, physical address). You agree to keep your
          contact information up to date. Please see our&nbsp;
          <$PrivacyPolicyLink />
          &nbsp; for more information on how we may contact you and how you may
          unsubscribe from these communications.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We may send you notices and messages about Along, either through the
            product itself or through the contact information you have provided.
            We will need you to keep your contact information up to date.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">2.6 Code of Conduct</$SubsectionTitle>

        <$Paragraph>
          You agree to follow our&nbsp;
          <$CodeOfConductLink />
          &nbsp;when using Along. These are rules that we’ve created to help
          maintain Along as a safe space for teachers and students.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Safety is a shared responsibility. By using Along, you agree to
            follow our&nbsp;
            <$CodeOfConductLink />
            &nbsp;when using Along. The guidelines in the Code of Conduct help
            us to keep Along a safe space for teachers and students.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">3. Rights and Limits</$SectionTitle>

        <$SubsectionTitle size="h3">3.1 Your License to Us</$SubsectionTitle>

        <$Paragraph>
          Our Services include features that require you (e.g. registration) and
          enable you (e.g. record a video) to provide information and content.
          As between you and us, you own the content and information that you
          submit or post using the Services, and you are only granting us the
          following non-exclusive license:
        </$Paragraph>
        <$Paragraph>
          A worldwide, transferable and sublicensable (through multiple tiers)
          right to use, copy, modify, publish, distribute, and process the
          content, feedback, and information that you provide through our
          Services (including surveys), without any further consent, notice,
          and/or compensation. The license rights you grant to us are limited in
          the following ways:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            If we agree in writing to additional restrictions on use with regard
            to any feedback, information, and content in the context of our
            collection of it, we will honor those limits.
          </$ListItem>
          <$ListItem>
            We will not include your content in advertisements for the products
            and services of third parties to others.
          </$ListItem>
          <$ListItem>
            We will get your additional consent if we want to give others the
            right to publish your content beyond the Services.
          </$ListItem>
          <$ListItem>
            By submitting suggestions or other feedback related to our Services
            to us, you agree that we can (but do not have to) use and share such
            feedback for any purpose without compensation to you. We will honor
            any limits we agree to at the time we collect feedback.
          </$ListItem>
          <$ListItem>
            If content includes personal information, it is subject to our&nbsp;
            <$PrivacyPolicyLink />.
          </$ListItem>
          <$ListItem>
            Because you own your content and information and we only have
            non-exclusive rights to it, you may choose to make it available to
            others.
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          You agree to only provide content or information that does not violate
          the law nor anyone’s rights (including intellectual property rights
          and privacy rights).
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Along allows you to share information and to upload content such as
            videos. It’s important that you only upload content and information
            that is yours and that abides by our policies. You own the content
            you submit or post, but grant us a license to use it for our
            service. Because you own your content and information, you may
            choose to make it available to others. We will not use your content
            or information in advertisements. For any personally identifiable
            information, the terms of our&nbsp;
            <$PrivacyPolicyLink />
            &nbsp;explain how we restrict our use and keep this information
            secure.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          3.2 Service Change and Availability
        </$SubsectionTitle>

        <$Paragraph>
          We may change, suspend, or end any aspect of the Services at our
          discretion. To the extent allowed under law, the changes will be
          effective without notice, but if they are material changes they will
          be effective upon notice provided to you directly or through the
          Services. You agree that we have no obligation to store, maintain, or
          provide you a copy of any content or information that you or others
          provide, except to the extent required by applicable law, or as
          otherwise provided in the User Agreement.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We are constantly working to improve Along for teachers and
            students. We have the right to change or suspend features at any
            time. For any material changes to the service, we will be sure to
            notify you before they take effect.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">3.3 Limits</$SubsectionTitle>

        <$Paragraph>
          We reserve the right to limit your use of the Services. If we believe
          that you may be in breach of this User Agreement or law or are
          misusing the Services (e.g., violating the&nbsp;
          <$CodeOfConductLink />
          ), we reserve the right to temporarily or permanently restrict,
          suspend, or terminate your access to the Services. If you believe your
          Services have been wrongfully terminated or restricted, contact us
          using the contact information provided in Section 9 below.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Safety is our top priority. If we believe that a user is in
            violation of our terms and guidelines, we have the right to change
            or limit access to Along.
          </$Paragraph>
        </$SummaryBox>

        <$SubsectionTitle size="h3">
          3.4 Intellectual Property Rights
        </$SubsectionTitle>

        <$Paragraph>
          Subject to the terms of this User Agreement, we grant you a limited
          right to access the Services, and we reserve all of our intellectual
          property rights and other proprietary rights in the Services. Using
          the Services does not give you any right, title, or interest in our
          Services or the content or information made available through our
          Services that is not already yours. Trademarks and logos used in
          connection with the Services are the trademarks of their respective
          owners. Our logos and other trademarks, service marks, graphics, and
          logos used for our Services are trademarks or registered trademarks of
          ours or our licensors.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Only the content and information you upload to Along is your own. We
            reserve all of our intellectual property rights, including
            trademarks and logos within Along.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          4. Disclaimer and Limit of Liability
        </$SectionTitle>

        <$SubsectionTitle size="h3">4.1 No Warranty</$SubsectionTitle>

        <$Paragraph>
          TO THE EXTENT ALLOWED UNDER LAW, WE (AND OUR SERVICE PROVIDERS AND
          LICENSORS) (A) DISCLAIM ALL IMPLIED WARRANTIES AND REPRESENTATIONS
          (E.G. WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          TITLE, AND NON-INFRINGEMENT, OR THAT THE DATA IS ACCURATE OR
          COMPLETE); (B) DO NOT GUARANTEE THAT THE SERVICES WILL FUNCTION
          WITHOUT INTERRUPTION OR ERRORS, AND (C) PROVIDE THE SERVICES
          (INCLUDING CONTENT AND INFORMATION) ON AN “AS IS” AND “AS AVAILABLE”
          BASIS.
        </$Paragraph>
        <$Paragraph>
          SOME LAWS DO NOT ALLOW CERTAIN DISCLAIMERS, SO SOME OR ALL OF THESE
          DISCLAIMERS MAY NOT APPLY TO YOU.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          4.2 Exclusion of Liability
        </$SubsectionTitle>

        <$Paragraph>
          TO THE EXTENT PERMITTED UNDER LAW (AND UNLESS WE HAVE ENTERED INTO A
          SEPARATE WRITTEN AGREEMENT THAT OVERRIDES OR AMENDS THIS CONTRACT), WE
          (AND OUR SERVICE PROVIDERS AND LICENSORS) SHALL NOT BE LIABLE TO YOU
          OR OTHERS FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR
          PUNITIVE DAMAGES, OR ANY LOSS OF DATA, OPPORTUNITIES, REPUTATION OR
          GOODWILL, PROFITS OR REVENUES, RELATED TO THE SERVICES (E.G. DOWN TIME
          OR LOSS, USE OF, OR CHANGES TO, YOUR INFORMATION OR CONTENT). IN NO
          EVENT SHALL GRADIENT LEARNING’S LIABILITY (AND THE LIABILITY OF OUR
          SERVICE PROVIDERS AND/OR LICENSORS), INCLUDING DIRECT DAMAGES EXCEED,
          IN THE AGGREGATE FOR ALL CLAIMS, ONE HUNDRED DOLLARS ($100).
        </$Paragraph>
        <$Paragraph>
          THIS LIMITATION OF LIABILITY IS PART OF THE BASIS OF THE BARGAIN
          BETWEEN YOU AND US AND SHALL APPLY TO ALL CLAIMS OF LIABILITY (E.G.
          WARRANTY, TORT, NEGLIGENCE, CONTRACT, LAW) AND EVEN IF WE AND OUR
          SERVICE PROVIDERS AND/OR LICENSORS HAVE BEEN TOLD OF THE POSSIBILITY
          OF ANY SUCH DAMAGE, AND EVEN IF THESE REMEDIES FAIL THEIR ESSENTIAL
          PURPOSE.
        </$Paragraph>
        <$Paragraph>
          THE LAWS IN SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR
          EXCLUSION OF LIABILITY, SO THESE LIMITS MAY NOT APPLY TO YOU. IF YOU
          ARE A NEW JERSEY RESIDENT, OR A RESIDENT OF ANOTHER U.S. STATE THAT
          PERMITS THE EXCLUSION OF THESE WARRANTIES AND LIABILITIES, THEN THE
          LIMITATIONS IN SECTION 4 SPECIFICALLY DO APPLY TO YOU.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            If legally permitted, you limit our liability for all damages to
            $100, and accept the Services as is, and without warranties.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">5. Termination</$SectionTitle>

        <$Paragraph>
          Either of us may terminate this User Agreement at any time with notice
          to the other. On termination, you lose the right to access or use the
          Services. The following shall survive termination:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Our rights to use and disclose your content and feedback related to
            the Services;
          </$ListItem>
          <$ListItem>Sections 2.5(2), 3, 4, 5, 6, 7, 8, and 9; and</$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We both have the right to terminate this contract at any time.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          6. Arbitration Agreement and Class Action Waiver
        </$SectionTitle>

        <$Paragraph>
          <strong>
            THIS SECTION CONSTITUTES AN ARBITRATION AGREEMENT (“Arbitration
            Agreement”); IT REQUIRES YOU AND GRADIENT LEARNING TO AGREE TO
            RESOLVE ALL DISPUTES BETWEEN US THROUGH BINDING INDIVIDUAL
            ARBITRATION, SO PLEASE READ IT CAREFULLY.
          </strong>
        </$Paragraph>

        <$SubsectionTitle size="h3">6.1 Applicability</$SubsectionTitle>

        <$Paragraph>
          In the unlikely event we end up in a legal dispute, you and Gradient
          Learning agree that all Disputes, including Enforceability Disputes,
          will be resolved exclusively in binding arbitration on an individual
          basis, except that you and Gradient Learning are not required to
          arbitrate IP Disputes. Notwithstanding the foregoing, either you or
          Gradient Learning may bring an individual action in small claims
          court.
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            A “<strong>Dispute</strong>” means a dispute, claim or controversy
            arising out of or relating to the Services or this User Agreement;
            or whether that dispute is (1) based on past, present or future
            events; and (2) in tort, contract, warranty, state, regulation, or
            other legal or equitable basis.
          </$ListItem>
          <$ListItem>
            An “<strong>Enforceability Dispute</strong>” means a Dispute
            relating to the interpretation, applicability, or enforceability of
            this Arbitration Agreement, including the formation of the contract,
            the arbitrability of any Dispute, and any claim that all or any part
            of this agreement is void or voidable.
          </$ListItem>
          <$ListItem>
            An “<strong>IP Dispute</strong>” means a Dispute relating to the
            ownership or enforcement of intellectual property rights.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">6.2 Waivers</$SubsectionTitle>

        <$UnorderedList>
          <$ListItem>
            <strong>Waiver of Jury Right</strong>. YOU AND GRADIENT LEARNING ARE
            EXPRESSLY GIVING UP ALL RIGHTS TO A JURY TRIAL OR COURT TRIAL BEFORE
            A JUDGE, EXCEPT AS EXPRESSLY PROVIDED IN THIS ARBITRATION AGREEMENT.
            The arbitrator’s decision will be final and binding on both you and
            us, subject to review solely on the grounds set forth in the Federal
            Arbitration Act (“<strong>FAA</strong>”).
          </$ListItem>
          <$ListItem>
            <strong>Waiver of Class or Consolidated Actions</strong>. YOU AND
            GRADIENT LEARNING AGREE THAT ALL DISPUTES MUST BE ARBITRATED OR
            LITIGATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS, COLLECTIVE
            ACTION, OR REPRESENTATIVE BASIS. The validity of this waiver – and
            whether an action may proceed as a class, collective, or
            representative action – must be decided by a court.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">6.3 Initiating a Dispute</$SubsectionTitle>

        <$Paragraph>
          To initiate a Dispute, a party must send to the other party written
          notice of that Dispute containing: (a) the name, address, and contact
          information of the party giving notice; (b) the facts giving rise to
          the Dispute; and (c) the relief requested. Notices sent to Gradient
          Learning must be sent by mail to the address provided in Section 9 of
          this User Agreement.
        </$Paragraph>
        <$Paragraph>
          You and we agree that the parties shall (in good faith) meet and
          attempt to resolve the Dispute within 30 days. If the Dispute is not
          resolved during that time period, then you and a representative of
          Gradient Learning shall (in good faith) meet and attempt to resolve
          the Dispute through non-binding mediation with a mutually agreed-upon
          mediator within 30 additional days. If you and we do not reach an
          agreement to resolve the dispute within that 60-day period, you or we
          may commence an arbitration proceeding or file a claim in small claims
          court.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          6.4 Arbitration Rules and Procedure
        </$SubsectionTitle>

        <$UnorderedList>
          <$ListItem>
            <strong>Rules</strong>. The Federal Arbitration Act governs the
            interpretation and enforcement of this Arbitration Agreement.
            Judicial Arbitration & Mediation Services, Inc. (“
            <strong>JAMS”</strong>) will administer the arbitration before a
            single arbitrator, and the arbitration will be initiated and
            conducted according to the Streamlined Arbitration Rules and
            Procedures (the “<strong>JAMS Rules</strong>”), to the extent they
            are not inconsistent with the terms of this Arbitration Agreement.
            The JAMS Rules and instructions about how to initiate an arbitration
            are available at&nbsp;
            <$ExternalLink
              href="https://www.jamsadr.com/rules-streamlined-arbitration"
              linkText="https://www.jamsadr.com/rules-streamlined-arbitration"
            />
            &nbsp;(as of the date of this agreement) or 1-800-352-5267.
          </$ListItem>
          <$ListItem>
            <strong>Fees</strong>. Pursuant to the JAMS Consumer Arbitration
            Minimum Standards, Gradient Learning will bear all costs of the
            arbitration (including any JAMS Case Management Fee and all
            professional fees for the arbitrator’s services), except for the
            filing fee if you are the party initiating the arbitration.
          </$ListItem>
          <$ListItem>
            <strong>Manner and Location of Arbitration</strong>. You may choose
            to have the arbitration conducted by telephone, in writing, online,
            or in person. If in person, you may choose to have the arbitration
            conducted (a) in San Mateo County, California, (b) in the county
            where you live, or (c) at another location that you and we agree
            upon.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">6.5 Opt out</$SubsectionTitle>

        <$Paragraph>
          You may opt out of this Arbitration Agreement by notifying us no later
          than 30 days after first becoming subject to it. Your notice must
          include your name, address, and a clear statement that you want to opt
          out of this Arbitration Agreement. Notices sent to Gradient Learning
          must be sent by mail to the address provided in Section 9 of this User
          Agreement.
        </$Paragraph>

        <$SubsectionTitle size="h3">6.6 Severability</$SubsectionTitle>

        <$Paragraph>
          If any portion of this Arbitration Agreement is found to be unlawful,
          void or for any reason unenforceable, then that portion shall be
          severed and the remainder of this Arbitration Agreement shall be given
          full force and effect.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            You agree that any disputes will be resolved through arbitration and
            only on an individual basis, in which case you agree to waive your
            right to a jury trial or court trial before a judge and to waive
            your right to participate in a class action lawsuit. To initiate a
            dispute, a written notice must be sent to the other party.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">7. General Terms</$SectionTitle>

        <$SubsectionTitle size="h3">7.1 Governing Law</$SubsectionTitle>

        <$Paragraph>
          This User Agreement and any Dispute between you and Gradient Learning
          will be governed by California law and/or applicable federal law
          (including the Federal Arbitration Act) without regard to its choice
          of law or conflicts of law principles, unless the School is a United
          States public and accredited educational institution, then any
          Disputes will be resolved using the laws of the state (within the
          United States) in which the School is domiciled and/or applicable
          federal law (including the Federal Arbitration Act), excluding
          conflicts of law rules.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          7.2 Jurisdiction and Venue
        </$SubsectionTitle>

        <$Paragraph>
          Subject to and without waiver of the arbitration provisions in Section
          6, you agree that any judicial proceedings (other than small claims
          actions) will be brought in and you hereby consent to the exclusive
          jurisdiction and venue in the state courts in the city and county of
          San Mateo, California, or federal court for the Northern District of
          California.
        </$Paragraph>

        <$SubsectionTitle size="h3">7.3 Severability</$SubsectionTitle>

        <$Paragraph>
          If any portion of this User Agreement is found to be unenforceable,
          you and we agree that the terms will be modified to make that part
          enforceable while still achieving its intent. If that is not possible,
          then the unenforceable part will be severed and the rest of this User
          Agreement enforced.
        </$Paragraph>

        <$SubsectionTitle size="h3">7.4 Entire Agreement</$SubsectionTitle>

        <$Paragraph>
          To the extent allowed by law, the English language version of this
          User Agreement is binding and other translations are for convenience
          only. This User Agreement (including any Data Privacy Addendum,
          the&nbsp;
          <$CodeOfConductLink />, any Addenda, and the&nbsp;
          <$PrivacyPolicyLink />) is the only agreement between us regarding the
          Services and supersedes all other related oral or written agreements
          or policies. This User Agreement shall govern the relationship between
          you and Gradient Learning. If there is a conflict between this User
          Agreement, any Addenda, the Privacy Policy, or any other agreement
          between you and Gradient (collectively, “Other Agreements”), the terms
          of this User Agreement shall apply and take precedence unless
          specifically provided otherwise in this User Agreement, Addenda,
          Privacy Policy, or the Other Agreements.
        </$Paragraph>

        <$SubsectionTitle size="h3">7.5 No Waiver</$SubsectionTitle>

        <$Paragraph>
          If we don’t act to enforce a breach of this User Agreement, that does
          not mean that we have waived our right to enforce this User Agreement.
          Any waiver by us must be in writing.
        </$Paragraph>

        <$SubsectionTitle size="h3">7.6 Assignment</$SubsectionTitle>

        <$Paragraph>
          You may not assign or transfer this User Agreement (or your use of
          Services) to anyone without our consent. However, you agree that
          Gradient Learning may assign this User Agreement in connection with a
          reorganization, or to a successor or assign or affiliate that agrees
          to assume our obligations under this User Agreement (and Privacy
          Policy) without your consent. There are no third-party beneficiaries
          to this User Agreement.
        </$Paragraph>

        <$SubsectionTitle size="h3">7.7 Notices</$SubsectionTitle>

        <$Paragraph>
          You agree that the only way to provide us legal notice is at the
          address provided in Section 9 of this User Agreement.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            This User Agreement and any disputes are governed by California law,
            unless your school is a United States public school, then any
            disputes will be resolved using the laws of your school’s state. Any
            judicial proceedings will take place in the state courts in the city
            and county of San Mateo, California or the federal court for the
            Northern District of California.
          </$Paragraph>
          <$Paragraph>
            If any part of the User Agreement is found to be unenforceable, it
            will be modified to be enforceable. If that isn’t possible, the
            unenforceable part will be removed, but the rest of the User
            Agreement will be enforced.
          </$Paragraph>
          <$Paragraph>
            You cannot transfer this User Agreement without our consent.
            Gradient Learning can assign this User Agreement to a successor, who
            will assume our obligations, without your consent.
          </$Paragraph>
          <$Paragraph>
            Any legal notices must be mailed to our address, provided in Section
            9.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          8. Complaints Regarding Content (DMCA)
        </$SectionTitle>

        <$Paragraph>
          We respect the intellectual property rights of others. We require that
          content posted by Users does not violate the intellectual property
          rights of third parties. Please see our&nbsp;
          <$DMCANotificationGuidelinesLink linkText="Digital Millennium Copyright Act (DMCA) Notification Guidelines" />
          &nbsp;for more information. If you believe your intellectual property
          rights have been violated through the Services, information on how to
          contact us is available in our <$DMCANotificationGuidelinesLink />.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We respect your intellectual property rights. If you believe any of
            your intellectual property rights have been violated through the
            Services, please find information on how to contact us in our&nbsp;
            <$DMCANotificationGuidelinesLink />.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">9. How To Contact Us</$SectionTitle>

        <$Paragraph>
          If you have any questions about this User Agreement, our&nbsp;
          <$PrivacyPolicyLink />
          ,&nbsp;
          <$CodeOfConductLink />, or our practices, please contact&nbsp;
          <$PrivacyAtAlongEmailLink />. To report misuse of your account, please
          contact&nbsp;
          <$SecurityAtAlongEmailLink />.
        </$Paragraph>
        <$Paragraph>
          You can learn more about Gradient Learning on our website,&nbsp;
          <$ExternalLink
            href="https://gradientlearning.org/"
            linkText="https://gradientlearning.org/"
          />
          .
        </$Paragraph>
        <$Paragraph>
          You can also reach us by mail at Gradient Learning ℅ Procopio at 818
          W. Seventh Street, Suite 930, Los Angeles, CA 90017, and by email
          at&nbsp;
          <$LegalAtAlongEmailLink />.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Please contact us if you have any questions at&nbsp;
            <$PrivacyAtAlongEmailLink />. To report misuse of your account,
            please contact&nbsp;
            <$SecurityAtAlongEmailLink />.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">10. Privacy and Security</$SectionTitle>

        <$Paragraph>
          Your privacy is extremely important to us, and we limit what we
          collect and how we use it. Please read our&nbsp;
          <$PrivacyPolicyLink />
          &nbsp;and&nbsp;
          <Link
            analyticsName={EventName.PolicyPages.CLICKED_USER_AGREEMENT_LINK}
            href="/user-agreement#exhibit-1"
            noExternalLinkIcon
          >
            Data Privacy Addendum
          </Link>
          &nbsp;which explains how we treat and protect personal information
          when you use the Services.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Your privacy is extremely important to us. In addition to the terms
            in this User Agreement, we encourage you to read our Privacy Policy
            and&nbsp;
            <Link
              analyticsName={EventName.PolicyPages.CLICKED_USER_AGREEMENT_LINK}
              href="/user-agreement#exhibit-1"
              noExternalLinkIcon
            >
              Data Privacy Addendum
            </Link>
            .
          </$Paragraph>
        </$SummaryBox>
        <$SectionTitle size="h1">
          <span id="exhibit-1">Exhibit 1 – Data Privacy Addendum</span>
        </$SectionTitle>
        <$Paragraph>
          <strong>
            Modeled after Version 2.0 of the Student Data Privacy Consortium’s
            Model Contract
          </strong>
        </$Paragraph>
        <$Paragraph>
          We have done our best to write this Data Privacy Addendum in simple
          and clear terms. In order to facilitate your understanding of the Data
          Privacy Addendum we have added notes that provide short summaries of
          the legal language in plain English.
        </$Paragraph>
        <$Paragraph>
          <strong>
            SCHOOL [by Registered Teacher as authorized agent on behalf of
            School] and GRADIENT LEARNING
          </strong>
        </$Paragraph>
        <$Paragraph>
          This&nbsp;<strong>DATA PRIVACY ADDENDUM</strong>&nbsp;(“
          <strong>Addendum</strong>”) is entered into by and between you on
          behalf of School (“<strong>School</strong>”) and&nbsp;
          <strong>Gradient Learning</strong>&nbsp;(formerly T.L.P. Education) (“
          <strong>Gradient Learning</strong>”), a California nonprofit public
          benefit corporation, having an address at 818 W. Seventh Street, Suite
          930, Los Angeles, CA 90017, on the Effective Date (“
          <strong>Parties</strong>”). The Parties agree to the terms as stated
          herein. Capitalized terms used but not defined herein shall have the
          meanings set forth in the User Agreement. This Addendum constitutes
          part of and should be read consistently with the User Agreement.
        </$Paragraph>

        <$SectionTitle size="h1">1. PURPOSE AND SCOPE</$SectionTitle>

        <$SubsectionTitle size="h3">1.1 Purpose of Addendum</$SubsectionTitle>

        <$Paragraph>
          The purpose of this Addendum is to describe the duties and
          responsibilities to protect Student Data (defined below) transmitted
          to Gradient Learning from the School and its Users pursuant to the
          User Agreement, including compliance with all applicable federal and
          state privacy statutes. This Addendum defines the base level of
          security. We regularly evaluate our policies and practices to improve
          the security of our network and systems and to respond to evolving
          best practices.
        </$Paragraph>
        <$Paragraph>
          As used in this Addendum, “<strong>Student Data</strong>” means any
          data, whether gathered by Gradient Learning or provided by School and
          its Users, that is directly related to a Student User, as more fully
          described in section 3 of the&nbsp;
          <$PrivacyPolicyLink />. Student Data includes Student User login
          credentials, passwords, Student User authentication tokens or security
          devices used for student Service or infrastructure access, and also
          constitutes Student Records for the purposes of this Addendum. “
          <strong>Student Records</strong>” includes any information that
          directly relates to a Student User that is maintained by School and
          any information acquired directly from the Student User through the
          use of instructional software or applications assigned to the Student
          User by School. Student Data does not include information that has
          been anonymized, De-Identified Data, or anonymous usage data regarding
          a student’s use of the Services. “<strong>De-Identified Data</strong>”
          is information that has all direct and Indirect Identifiers removed
          such that the data cannot reasonably be used to identify or contact a
          Student User. Finally, “<strong>Indirect Identifiers</strong>” means
          any information that, either alone or in aggregate, would allow a
          reasonable person to be able to identify a Student User to a
          reasonable certainty. When anonymous or non-personal information is
          directly or indirectly linked with personal information, this
          anonymous or non-personal information is also treated as personal
          information. Persistent identifiers that are not anonymized,
          de-identified or aggregated are personal information.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          1.2 Nature of Services Provided
        </$SubsectionTitle>

        <$Paragraph>
          Pursuant to and as fully described in the User Agreement, Gradient
          Learning has agreed to provide Along and any other products and
          services that Gradient Learning may choose to provide now or in the
          future (collectively, the “<strong>Services</strong>”).
        </$Paragraph>

        <$SubsectionTitle size="h3">
          1.3 Student Data to Be Provided
        </$SubsectionTitle>

        <$Paragraph>
          In order to use the Services, School and its Users may provide the
          categories of Student Data described in section 3 of the{" "}
          <$PrivacyPolicyLink />.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            This Addendum describes how we are both required to protect student
            data, including personally identifiable information of a student.
            Student data means any information related to a student. It does not
            include de-identified data, which is data that has had all
            identifying information removed so it cannot reasonably be used to
            identify or contact a student. The&nbsp;
            <$PrivacyPolicyLink />
            &nbsp;identifies the specific types of student data and personally
            identifiable information that may be provided when using the
            services.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">
          2. DATA OWNERSHIP AND AUTHORIZED ACCESS
        </$SectionTitle>

        <$SubsectionTitle size="h3">
          2.1 Student Data Property of School
        </$SubsectionTitle>

        <$Paragraph>
          All Student Data or any other Student Records transmitted to Gradient
          Learning pursuant to the User Agreement are and will continue to be
          the property of, and under the control of, the School, or the party
          who provided such Student Data or Student Records (such as the student
          or parent). The Parties agree that as between them, all rights,
          including all intellectual property rights in and to Student Data or
          any other Student Records contemplated per the Agreement shall remain
          the exclusive property of the School or the party who provided such
          Student Data or Student Records (such as the student or parent). This
          Addendum is governed by the Family Educational Rights and Privacy Act,
          20 U.S.C. 1232g (“
          <strong>FERPA</strong>”). For the purposes of FERPA, to the extent
          Personally Identifiable Information (as defined below) from Education
          Records (as defined by FERPA) is transmitted to Gradient Learning from
          School, Gradient Learning shall be considered a School Official
          (defined below) with a legitimate educational interest, under the
          direct control of the School as it pertains to the use of Education
          Records notwithstanding the above. Gradient Learning shall, at the
          School’s request, provide for review of Student Data or Student
          Records within thirty (30) days following a written request.
        </$Paragraph>
        <$Paragraph>
          “<strong>School Official</strong>” means, for the purposes of this
          Addendum and pursuant to FERPA, a contractor that: (1) performs an
          institutional service or function for which the agency or institution
          would otherwise use employees; (2) is under the direct control of the
          agency or institution with respect to the use and maintenance of
          Education Records; and (3) is subject to FERPA’s terms governing the
          use and re-disclosure of Personally Identifiable Information from
          Education Records. “
          <strong>Personally Identifiable Information</strong>” means data that
          can be used to identify or contact a particular individual, including
          direct and Indirect Identifiers and the types of information described
          in the&nbsp;
          <$PrivacyPolicyLink />.
        </$Paragraph>
        <$Paragraph>
          To the extent this Addendum is governed by the Children’s Online
          Privacy Protection Act, 15 U.S.C. 6501-6502 (“COPPA”), the School
          consents to the collection of Student Data as provided in the User
          Agreement, this Addendum, and our Privacy Policy. Gradient Learning
          agrees to use Student Data solely for the use and benefit of the
          School, and for no other commercial purpose.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          2.2 Parent or Legal Guardian Access
        </$SubsectionTitle>

        <$Paragraph>
          As set forth in applicable law, School (including the Registered
          Teacher as the School’s representative, see section 3.4 below) shall
          establish reasonable procedures by which a parent, legal guardian, or
          eligible student may review and request amendment of the related
          student’s Student Records and correct erroneous information,
          consistent with the functionality of the Service. Gradient Learning
          shall respond within 30 days to the School’s written request for
          Student Records held by Gradient Learning to view or amend as
          necessary. In the event that a parent or legal guardian of a student
          or other individual contacts Gradient Learning to review any of the
          Student Records or Student Data accessed pursuant to the Services,
          Gradient Learning shall refer the parent, legal guardian or individual
          to the School. In such an event, School shall follow the necessary and
          proper procedures regarding the requested information.
        </$Paragraph>

        <$SubsectionTitle size="h3">2.3 Third Party Request</$SubsectionTitle>

        <$Paragraph>
          Should any person other than Gradient Learning, School, a User, or a
          Service Provider (“<strong>Third Party</strong>”), contact Gradient
          Learning with a request for Student Data held by Gradient Learning
          pursuant to the Services, Gradient Learning shall redirect the Third
          Party (including law enforcement and government entities) to request
          the Student Data directly from the School. Gradient Learning shall
          notify the School in advance of any compelled disclosure to a Third
          Party, unless legally prohibited. For purposes of this Addendum, a
          Service Provider is someone who Gradient Learning uses for data
          collection, analytics, storage, or other service to operate and/or
          improve the Service, and who has access to Personally Identifiable
          Information including Student Data.
        </$Paragraph>

        <$SubsectionTitle size="h3">2.4 No Unauthorized Use</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning shall not use Personally Identifiable Information
          from Student Data, or in a Student Record, for any purpose other than
          as explicitly specified in the User Agreement.
        </$Paragraph>

        <$SubsectionTitle size="h3">2.5 Service Providers.</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning may use Service Providers in order to perform its
          duties under the User Agreement. Gradient Learning shall enter into
          written agreements with all Service Providers and shall be responsible
          for any actions of Service Providers that would be a breach of this
          Addendum.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            We will not use student personal information in any unauthorized way
            other than as stated in this Agreement. Student information and
            student records submitted through Along will remain the property of
            the school or the person who provided them. Any requests by parents,
            legal guardians and eligible students to review and correct student
            records must be made through the school. It’s the school’s
            responsibility to provide a reasonable procedure for this review.
            Third party requests for student information will be directed to the
            school.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">3. DUTIES OF SCHOOL</$SectionTitle>

        <$SubsectionTitle size="h3">
          3.1 Provide Data In Compliance With Applicable Law
        </$SubsectionTitle>
        <$Paragraph>
          School shall provide Student Data or any other Student Records, for
          the purposes of the User Agreement in compliance with any applicable
          state or federal laws and regulations (including FERPA and the
          Children’s Online Privacy Protection Act, 15 U.S.C. 6501-6502 “
          <strong>COPPA</strong>”) pertaining to data privacy and security
          applicable to School. If School provides Education Records to Gradient
          Learning, School represents, warrants, and covenants to Gradient
          Learning, as applicable, that School has:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            complied with all applicable provisions of FERPA relating to
            disclosures to school officials with a legitimate educational
            interest, including, without limitation, informing parents in their
            annual notification of FERPA rights that the School defines “school
            official” to include service providers and defines “legitimate
            educational interest” to include services such as the type provided
            by Gradient Learning; or
          </$ListItem>
          <$ListItem>
            obtained all necessary parental or eligible student written consent
            or provided all necessary notice to share the Student Data with
            Gradient Learning, in each case, solely to enable Gradient
            Learning’s operation of the Services.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">
          3.2 Reasonable Precautions
        </$SubsectionTitle>

        <$Paragraph>
          School shall take reasonable precautions to secure usernames,
          passwords, and any other means of gaining access to the Services and
          Student Data in accordance with the User Agreement, this Addendum and
          applicable law.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          3.3 Unauthorized Access Notification
        </$SubsectionTitle>

        <$Paragraph>
          School shall notify Gradient Learning immediately of any known or
          suspected unauthorized use or access of the Services or Student Data.
          School will assist Gradient Learning in any efforts by Gradient
          Learning to investigate and respond to any unauthorized use or access.
        </$Paragraph>

        <$SubsectionTitle size="h3">3.4 School Representative</$SubsectionTitle>

        <$Paragraph>
          The Registered Teacher entering into the User Agreement shall serve as
          the representative of the School for the coordination and fulfillment
          of the duties of this Addendum.
        </$Paragraph>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            The school agrees to provide student information in ways that follow
            state and federal laws, including FERPA. The school will take
            precautions to keep usernames, passwords, and accounts secure. If
            there is a breach in the security of accounts or student
            information, the school will notify Gradient Learning immediately
            and assist Gradient Learning in investigating and responding to
            unauthorized use or access. The Registered Teacher will serve as a
            representative of the school.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">4. DUTIES OF GRADIENT LEARNING</$SectionTitle>

        <$SubsectionTitle size="h3">4.1 Privacy Compliance</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning shall comply with all state and federal laws and
          regulations related to privacy and security and applicable to Schools
          and/or Gradient Learning in providing the Services to School.
        </$Paragraph>

        <$SubsectionTitle size="h3">4.2 Authorized Use</$SubsectionTitle>

        <$Paragraph>
          Student Data shared pursuant to the User Agreement, including
          persistent unique identifiers that are personally identifiable, shall
          be used for no purpose other than the Services, for the uses set forth
          in the User Agreement, for School support and development across and
          among Gradient Learning platforms and services, and/or as otherwise
          legally permissible. The foregoing limitation does not apply to any
          De-Identified Data.
        </$Paragraph>

        <$SubsectionTitle size="h3">4.3 Staff Obligation</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning shall require all employees, staff, agents, and
          Service Providers who have access to Student Data to comply with all
          applicable laws with respect to the Student Data shared under the User
          Agreement. Gradient Learning agrees to require and maintain written
          confidentiality obligations from each of its employees, staff, agents,
          or Service Providers with access to Student Data pursuant to the User
          Agreement.
        </$Paragraph>

        <$SubsectionTitle size="h3">4.4 No Disclosure</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning shall not disclose any Student Data obtained under
          the User Agreement in a manner that directly identifies an individual
          student to any other entity except as authorized by the User
          Agreement, or as required by law. Gradient Learning will not Sell
          Student Data. “<strong>Sell</strong>” (consistent with the Student
          Online Privacy Protection Act, California Business and Professions
          Code section 22584 (“<strong>SOPIPA</strong>”) and the Student Privacy
          Pledge) does not include or apply to the purchase, merger, or other
          type of acquisition of a company by another entity, provided that the
          company or successor entity continues to treat the personal
          information in a manner consistent with the Student Privacy Pledge
          with respect to the previously acquired personal information.
          Additionally, Gradient Learning will not disclose, trade, or transfer
          Student Data to any third parties, except with the prior written
          consent of the School. The prohibition on disclosing, trading, or
          transferring Student Data does not apply to the access to or
          disclosure of Student Data (a) to School or School Administrators, (b)
          to authorized Users, (c) to parents or legal guardians, (d) to and
          among affiliated Gradient Learning organizations for the purposes of
          providing and improving Partner School support, product analytics and
          development, and other internal usage as permitted by law, (e) as
          authorized by a parent, legal guardian or eligible student, (f) as
          permitted by law or (g) to Service Providers, in connection with
          operating or improving the Services. The list of Gradient Learning’s
          Service Providers can be accessed through the&nbsp;
          <$PrivacyPolicyLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">4.5 De-Identified Data</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning may create De-Identified Data, and De-Identified
          Data may be used for any lawful purpose including, but not limited to,
          operating, improving, and developing Gradient Learning’s educational
          sites, services, or applications. Gradient Learning’s use of such
          De-Identified Data shall survive termination of this Addendum or any
          request by School to return or destroy Student Data. Gradient Learning
          agrees not to attempt or have any third party attempt to re-identify
          De-Identified Data except for the sole purpose of validating Gradient
          Learning’s de-identification processes.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          4.6 Disposition of Student Data and Student Records
        </$SubsectionTitle>

        <$Paragraph>
          Gradient Learning will delete User accounts if we receive a written
          request from a Student User, Registered Teacher, or School
          Administrator. However, some information may remain on logs or
          encrypted backup storage copies until they are deleted. Further,
          Gradient Learning may retain information to comply with our legal
          obligations or to protect the safety and security of our Users or our
          Services, for example, in cases of past policy and content violations
          or due to a request from law enforcement. Such information will be
          disposed or or deleted when it is no longer needed for the purpose for
          which it was retained. Disposition shall include (1) the shredding of
          any hard copies of any Personally Identifiable Information contained
          in Student Data and Student Records; (2) erasing any Personally
          Identifiable Information contained in Student Data and Student
          Records; or (3) otherwise modifying the Personally Identifiable
          Information contained in Student Data and Student Records to make it
          unreadable, indecipherable or de-identified. Gradient Learning shall
          provide written notification to the School’s representative when the
          Personally Identifiable Information contained in the Student Data and
          Student Records has been disposed. The duty to dispose of Student Data
          and Student Records shall not extend to De-Identified Data.
        </$Paragraph>
        <$Paragraph>
          Deletion Request from Student User: If a Student User requests
          deletion of a Student Account, that account will be removed from Along
          after verifying the request. In parallel, Gradient Learning shall
          notify the Registered Teacher and the School Administrator who will
          have thirty (30) days from the date of receiving such notice to
          request any Student Data and Student Records from the Student Account
          (“<strong>Transfer Period</strong>”). Gradient Learning shall dispose
          of or delete the Student Account thirty (30) days after the end of the
          Transfer Period.
        </$Paragraph>
        <$Paragraph>
          Deletion Request from Registered Teacher: If a Registered Teacher
          requests deletion of a Student Account (on behalf of a Student User or
          Parent), or the account of the Registered Teacher and all associated
          Student Accounts, those accounts will be removed from Along after
          verifying the request. In parallel, Gradient Learning shall notify the
          School Administrator. Gradient Learning shall dispose of or delete the
          account(s) thirty (30) days after the end of the Transfer Period.
        </$Paragraph>
        <$Paragraph>
          Deletion Request from School Administrator: If a School Administrator
          requests the deletion of any accounts associated with their School,
          Gradient Learning shall delete such accounts, or dispose of or delete
          all Personally Identifiable Information contained in Student Data and
          Student Records thirty (30) days after verifying such written request
          by the School Administrator, or as required by law, and according to a
          schedule and procedure as Gradient Learning and the School may
          reasonably agree.
        </$Paragraph>
        <$Paragraph>
          Transfer of Student Data and Student Records: If a written request is
          received from a School Administrator prior to a deletion request or
          during the Transfer Period, Gradient Learning shall transfer the
          Student Data and Student Records to the School Administrator or other
          designee within thirty (30) days of verifying such written request by
          the School Administrator, or as required by law, and according to a
          schedule and procedure as Gradient Learning and the School
          Administrator may reasonably agree. If no written request is received
          during the Transfer Period, Gradient Learning shall delete the User
          Account, or dispose of or delete all Personally Identifiable
          Information contained in Student Data and Student Records at the
          earliest of (a) thirty days after the end of the Transfer Period; (b)
          when it is no longer needed for the purpose for which it was obtained
          or (c) as required by applicable law.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          4.7 Advertising Prohibition
        </$SubsectionTitle>

        <$Paragraph>
          Gradient Learning shall not use Personally Identifiable Information
          contained in Student Data to (a) serve Behaviorally Targeted
          Advertising (defined below) to Student Users or their Parents or any
          other user; or (b) develop a profile of a Student User or any other
          user for any commercial purpose other than providing the Services to
          School or as set forth in the User Agreement. Gradient Learning shall
          not use or disclose Personally Identifiable Information contained in
          Student Data for Third-Party Advertising.
        </$Paragraph>
        <$Paragraph>
          As used in this Addendum, “
          <strong>Behaviorally Targeted Advertising</strong>” means presenting
          an advertisement to a Student User where the selection of the
          advertisement is based on Student Data or Student-Generated Content or
          inferred over time from the usage of Gradient Learning’s website,
          online service or mobile application by such student or the retention
          of such Student User’s online activities or requests over time and
          across non-affiliate websites for the purpose of targeting subsequent
          advertising. “<strong>Student-Generated Content</strong>” means
          materials or content created by a Student User during and for the
          purpose of education including, but not limited to, essays, research
          reports, portfolios, creative writing, music or other audio files,
          photographs, videos, and account information that enables ongoing
          ownership of such content. “<strong>Third-Party Advertising</strong>”
          means direct advertising of third parties (i.e. not Users) and their
          products or services on our Services (e.g., such as when an advertiser
          would bid to place an advertisement directly on a platform). Gradient
          Learning does not allow third parties to advertise directly on its
          Services in user logged in areas of the Services, nor does Gradient
          Learning sell advertising space in logged in areas on the Service.
          Gradient Learning also does not use third-party ad servers (such as
          Google AdWords or AdSense) in user logged in areas of the Service.
          Note that the Along Teacher Library contains content provided by third
          party experts—specifically nonprofit organizations focused on
          education. Such content may include references and links to services
          provided by the nonprofit organizations whose privacy practices may
          differ from ours.
        </$Paragraph>

        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Students’ personal information will only be used as needed to
            provide educational services. Gradient Learning will not share or
            sell students’ personal information or use it for targeted
            advertising. Schools can request that all personal information be
            deleted or disposed of. De-identified information (data that has had
            all identifying information removed so it cannot reasonably be used
            to identify or contact a student) can be used for any lawful
            purpose.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">5. DATA PROVISIONS</$SectionTitle>

        <$Paragraph>
          Gradient Learning’s core security commitments are set forth below and
          we commit to maintaining this baseline. For more information regarding
          Gradient Learning’s current security practices, see the&nbsp;
          <$SecurityWhitepaperLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">5.1 Data Security</$SubsectionTitle>

        <$Paragraph>
          Gradient Learning agrees to store and process data by employing
          administrative, physical, and technical safeguards designed to protect
          Student Data from unauthorized access, disclosure, and use or
          acquisition by an unauthorized person, including when transmitting and
          storing such information. Currently, Gradient Learning implements
          security practices identified in our&nbsp;
          <$SecurityWhitepaperLink />. These measures shall include, but are not
          limited to:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Gradient Learning shall implement strong authentication methods
            including multi-factor authentication (MFA) with strong password
            complexity for all employees and contractors. These methods meet or
            exceed Article 4.3 of NIST 800-63-3.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall limit access to Student Data to employees,
            agents, staff, and Service Providers who need access in order for
            Gradient Learning to provide the Services. To the extent permissible
            by law, Gradient Learning shall conduct criminal background checks
            of employees prior to providing access to Student Data and prohibit
            access to Student Data by any person with criminal or other relevant
            unsatisfactory information that presents an unreasonable risk to
            School or its Student Users.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall destroy or delete all Personally
            Identifiable Information contained in Student Data obtained under
            the User Agreement as set forth in Section 4.6 hereof.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall employ a strong modern encryption technology
            designed to securely transmit (encryption in transit) and store
            (encryption at rest) all Student Data. Gradient Learning shall
            maintain all Student Data obtained or generated pursuant to the User
            Agreement in a secure computing environment and shall not copy,
            reproduce, or transmit data obtained pursuant to the User Agreement,
            except as necessary to fulfill the purpose of data requests by
            School or as otherwise set forth in the User Agreement.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall create a secured data backup and recovery
            capability that is designed to ensure an effective, timely and
            accurate restoration of all Student Data. The capability will be
            designed to minimize the amount of Student Data loss in the event of
            some form of catastrophic failure. For further protection, those
            backups will be encrypted and are stored in a different region.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall adopt and maintain a secure software
            development lifecycle which will incorporate security practices such
            as penetration testing, code reviews and architecture analysis as
            essential functions of the development effort. Any identified
            security vulnerability will be remediated in a timely manner.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall provide periodic security training to those
            of its employees and staff who have access to Student Data.
          </$ListItem>
          <$ListItem>
            Gradient Learning shall enter into written agreements whereby
            Service Providers agree to prevent unauthorized access and use of
            Student Data in a manner consistent with the terms of this Section
            5.1. Gradient Learning shall periodically review such compliance of
            Service Providers.
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          In the event Schools have questions regarding Data Privacy or
          Security, they may contact our team at&nbsp;
          <$PrivacyAtAlongEmailLink />. Vulnerabilities can be responsibly
          disclosed by contacting&nbsp;
          <$SecurityAtAlongEmailLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          5.2 Incident Response and Security Governance
        </$SubsectionTitle>

        <$Paragraph>
          In addition to those security measures described in Section 5.1,
          Gradient Learning also implements an incident response and security
          governance program, which:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Maintains availability for the Services through event monitoring and
            response procedures for all site outages or any observable
            occurrences, including automated site outage notifications.
          </$ListItem>
          <$ListItem>
            Implements incident response policies, plans and procedures focused
            on timely and effective incident response.
          </$ListItem>
          <$ListItem>
            Restricts network and physical access to infrastructure for the
            Services. We also leverage services to monitor for suspicious
            activity and employ professionals with training in security incident
            detection and response. More information about our infrastructure
            can be found in the&nbsp;
            <$SecurityWhitepaperLink />.
          </$ListItem>
          <$ListItem>
            Implements oversight and governance procedures for security risks
            and vulnerabilities, including a Vulnerability Disclosure Program
            and mandatory reviews of any incidents affecting the Services.
          </$ListItem>
        </$UnorderedList>

        <$SubsectionTitle size="h3">5.3 Data Breach</$SubsectionTitle>

        <$Paragraph>
          In the event that Gradient Learning becomes aware of an unauthorized
          disclosure of or access to Student Data (a “
          <strong>Security Incident</strong>”), Gradient Learning shall provide
          notice to the School without undue delay or as required by the
          applicable state law (each, a “
          <strong>Security Incident Notification</strong>”).
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Unless otherwise required by the applicable law, the Security
            Incident Notification shall be written in plain language, shall be
            titled “Notice of Data Breach,” and shall present the information
            described herein under the following headings: “What Happened,”
            “What Information Was Involved,” “What We Are Doing,” “What You Can
            Do,” and “For More Information.” Additional information may be
            provided as a supplement to the notice.
          </$ListItem>
          <$ListItem>
            The Security Incident Notification described above in Section 5.2(a)
            shall include such information required by the applicable state law
            and the following information:
            <$UnorderedList>
              <$ListItem>
                The name and contact information of the reporting School subject
                to this section.
              </$ListItem>
              <$ListItem>
                A list of the types of Personally Identifiable Information that
                were or are reasonably believed to have been the subject of the
                Security Incident.
              </$ListItem>
              <$ListItem>
                If the information is known at the time the Security Incident
                Notification is provided, then either (1) the date of the
                Security Incident, (2) the estimated date of the Security
                Incident, or (3) the date range within which the Security
                Incident occurred. The Security Incident Notification shall also
                include the date of the notice.
              </$ListItem>
              <$ListItem>
                Whether, to the knowledge of Gradient Learning at the time
                notice is provided, the notification was delayed as a result of
                a law enforcement investigation or request.
              </$ListItem>
              <$ListItem>
                A general description of the Security Incident, if that
                information is possible to determine at the time the notice is
                provided.
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            At Gradient Learning’s discretion, the Security Incident
            Notification may also include any of the following:
            <$UnorderedList>
              <$ListItem>
                Information about what Gradient Learning has done to protect
                individuals whose Personally Identifiable Information has been
                breached by the Security Incident.
              </$ListItem>
              <$ListItem>
                Advice on steps that the person whose Personally Identifiable
                Information has been breached may take to protect themselves.
              </$ListItem>
            </$UnorderedList>
          </$ListItem>
          <$ListItem>
            To the extent required by the applicable state law, Gradient
            Learning shall seek to notify the affected parent, legal guardian or
            eligible student of the Security Incident, which shall include as
            applicable the information listed in subsections (b) and (c), above.
          </$ListItem>
        </$UnorderedList>
        <$SummaryBox>
          <$SummaryBoxTitle size="h2">Summary</$SummaryBoxTitle>
          <$Paragraph>
            Gradient Learning will work to protect student information from
            unauthorized access by employing administrative, physical, and
            technical safeguards. These include:
          </$Paragraph>
          <$UnorderedList>
            <$ListItem>using strong authentication methods,</$ListItem>
            <$ListItem>limiting who has access to student data,</$ListItem>
            <$ListItem>
              destroying or deleting personal information as needed,
            </$ListItem>
            <$ListItem>using strong encryption technology,</$ListItem>
            <$ListItem>
              using secured data backup and recovery capability,
            </$ListItem>
            <$ListItem>
              maintaining a secure software development lifecycle,
            </$ListItem>
            <$ListItem>
              providing security training to employees and staff, and
            </$ListItem>
            <$ListItem>
              requiring Service Providers to follow similar terms.
            </$ListItem>
          </$UnorderedList>
          <$Paragraph>
            If there is a data breach, Gradient Learning will provide notice to
            the school as required by law.
          </$Paragraph>
        </$SummaryBox>

        <$SectionTitle size="h1">6. MISCELLANEOUS</$SectionTitle>

        <$SubsectionTitle size="h3">6.1 Term</$SubsectionTitle>

        <$Paragraph>
          Except as otherwise stated herein, Gradient Learning shall be bound by
          this Addendum for the duration of the User Agreement or a longer
          period as required by law.
        </$Paragraph>

        <$SubsectionTitle size="h3">6.2 Termination</$SubsectionTitle>

        <$Paragraph>
          Except as otherwise stated herein, Gradient Learning shall be bound by
          this Addendum for the duration of the User Agreement or a longer
          period as required by law.
        </$Paragraph>

        <$SubsectionTitle size="h3">6.3 Effect of Termination</$SubsectionTitle>

        <$Paragraph>
          If the User Agreement is terminated and no other agreement between
          School and Gradient Learning is in effect through other Registered
          Teachers or School Administrators, Gradient Learning shall dispose of
          all of School’s Personally Identifiable Information contained in
          Student Data pursuant to Section 4.6.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          6.4 Priority of Agreements
        </$SubsectionTitle>

        <$Paragraph>
          This Addendum shall govern the treatment of Student Data. With respect
          to the treatment of Student Data, in the event there is conflict
          between the terms of this Addendum and the User Agreement, or any
          other agreement between the School and Gradient Learning, the terms of
          this Addendum shall apply and take precedence. Except as described in
          this paragraph, all other provisions of the User Agreement shall
          remain in effect.
        </$Paragraph>

        <$SubsectionTitle size="h3">6.5 Notice</$SubsectionTitle>

        <$Paragraph>
          All notices or other communication required or permitted to be given
          hereunder must be sent to School or Gradient Learning, as applicable,
          as provided in the User Agreement.
        </$Paragraph>

        <$SectionTitleBold size="h1" textAlign="center">
          Appendix A – Certain State-Specific Terms, to Exhibit 1, Data Privacy
          Addendum
        </$SectionTitleBold>

        <$Paragraph>
          This document, Appendix A, Certain State-Specific Terms (“Appendix”)
          is incorporated into Exhibit 1, Data Privacy Addendum (“DPA”) of the
          User Agreement available at&nbsp;
          <Link
            analyticsName={EventName.PolicyPages.CLICKED_USER_AGREEMENT_LINK}
            href="/user-agreement#exhibit-1"
            noExternalLinkIcon
          >
            https://app.along.org/user-agreement/#exhibit-1
          </Link>
          &nbsp; and the Privacy Policy available at&nbsp;
          <$PrivacyPolicyLink linkText="https://app.along.org/privacy-policy/" />
          &nbsp; (collectively, the “Agreement”). Capitalized terms used but not
          defined herein shall have the meanings set forth in the Agreement.
        </$Paragraph>

        <$SectionTitleBold size="h1">California</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by California AB
          1584, codified at California Education Code § 49073.1, and includes
          the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            As set forth in section 2.1 of the Along DPA, pupil records continue
            to be the property of and under the control of the School;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.2 of the Along DPA, any information in the
            pupil record shared with Gradient Learning pursuant to the User
            Agreement, shall be used for no purpose other than those required or
            specifically permitted by the Along User Agreement and the Along
            Privacy Policy;
          </$ListItem>
          <$ListItem>
            A parent, legal guardian, or eligible pupil may review personally
            identifiable information in the pupil’s records that is collected by
            Gradient Learning, and correct erroneous information, as set forth
            in section 2.2 of the Along DPA and section 4 of the Along Privacy
            Policy;
          </$ListItem>
          <$ListItem>
            As set forth in section 5.1 of the Along DPA and section 6 of the
            Along Privacy Policy, Gradient Learning shall provide periodic
            security training designed to ensure the security and
            confidentiality of pupil records to those of its employees and staff
            who have access to pupil records;
          </$ListItem>
          <$ListItem>
            As set forth in section 5.3 of the Along DPA, Gradient Learning will
            notify the School when there has been an unauthorized release,
            disclosure or acquisition of pupil records; and to the extent
            required by Cal. Civ. Code § 1792.82 et seq, Gradient Learning, in
            coordination with the School as appropriate, shall seek to notify
            the affected parent, legal guardian or eligible pupil;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.6 of the Along DPA and section 7 of the
            Along Privacy Policy, a pupil’s records shall not be retained or
            available to Gradient Learning upon completion of the terms of the
            Along User Agreement;
          </$ListItem>
          <$ListItem>
            As set forth in section 2.1 and section 3.1 of the Along DPA,
            Gradient Learning and School, shall ensure compliance with the
            Family Educational Rights and Privacy Act (20 U.S.C. Sec. 1232g);
            and
          </$ListItem>
          <$ListItem>
            As set forth in section 4.7 of the Along DPA and section 3.3. Of the
            Along Privacy Policy, Gradient Learning shall not use personally
            identifiable information in pupil records to engage in targeted
            advertising.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Colorado</$SectionTitleBold>

        <$Paragraph>
          Gradient Learning complies with all applicable requirements of
          Colorado’s Student Data Transparency and Security Act, C.R.S.
          22-16-101, et seq.
        </$Paragraph>

        <$SectionTitleBold size="h1">Connecticut</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by the Connecticut
          Act Concerning Student Data Privacy, Conn. Gen. Stat. Ann. §
          10-234aa-dd, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            As set forth in section 2.1 of the Along DPA, Student Data, which
            includes student information, student records and student-generated
            content, are not the property of or under the control of Gradient
            Learning;
          </$ListItem>
          <$ListItem>
            Section 4.6 of the Along DPA sets forth the means by which School
            may request the deletion of any Student Data in the possession of
            the contractor that is not (A) otherwise prohibited from deletion or
            required to be retained under state or federal law, or (B) stored as
            a copy as part of a disaster recovery storage system and that is (i)
            inaccessible to the public, and (ii) unable to be used in the normal
            course of business by Gradient Learning;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.2 of the Along DPA, Gradient Learning
            shall not use Student Data for any purposes other than those
            authorized pursuant to the Agreement. The Along User Agreement and
            the Along Privacy Policy set forth the exclusive purposes for which
            the Student Data will be used by Gradient Learning;
          </$ListItem>
          <$ListItem>
            A student, parent or legal guardian of a student may review
            personally identifiable information contained in Student Data
            collected by Gradient Learning, and correct erroneous information,
            if any, in such student record as set forth in section 2.2 of the
            Along DPA and section 4 of the Along Privacy Policy;
          </$ListItem>
          <$ListItem>
            Gradient Learning’s core security commitments designed to ensure the
            security and confidentiality of Student Data are set forth in
            section 5.1 of the Along DPA and outlined in section 6 of the Along
            Privacy Policy;
          </$ListItem>
          <$ListItem>
            As set forth in section 5.3 of the Along DPA, Gradient Learning will
            notify the School when there has been an unauthorized release,
            disclosure or acquisition of Student Data;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.6 of the Along DPA and section 7 of the
            Along Privacy Policy, Student Data shall not be retained or
            available to Gradient Learning upon termination of the User
            Agreement;
          </$ListItem>
          <$ListItem>
            As set forth in section 2.1 and section 3.1 of the Along DPA,
            Gradient Learning and School, shall ensure compliance with the
            Family Educational Rights and Privacy Act of 1974, 20 USC 1232g, as
            amended from time to time;
          </$ListItem>
          <$ListItem>
            As set forth in section 7.1 of the Along User Agreement, the laws of
            the state of Connecticut shall govern the rights and duties of
            Gradient Learning and the School; and
          </$ListItem>
          <$ListItem>
            As set forth in section 7.3 of the Along User Agreement, if any
            provision of the Agreement or the application of the Agreement is
            held invalid by a court of competent jurisdiction, the invalidity
            does not affect other provisions or applications of the Agreement
            which can be given effect without the invalid provision or
            application.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Illinois</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by the Illinois
          Student Online Personal Protection Act (“SOPPA”), codified at 105 ILCS
          85/5, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            A listing of the categories or type of information to be provided to
            Gradient Learning is available for public review in section 3.1 of
            the Along Privacy Policy;
          </$ListItem>
          <$ListItem>
            Pursuant to and as fully described in the User Agreement, Gradient
            Learning has agreed to provide Along, including its&nbsp;
            <$ExternalLink
              href="https://www.along.org"
              linkText="website"
              title="www.along.org"
            />
            , features, and related services accessed through Along
            (collectively “Services”) to the School;
          </$ListItem>
          <$ListItem>
            Pursuant to section 2.1 of the Along DPA, in performing its
            obligations under the Agreement, Gradient Learning is acting as a
            school official with a legitimate educational interest; is
            performing an institutional service or function for which the School
            would otherwise use its own employees; is under the direct control
            of the School with respect to the use and maintenance of Student
            Data; and is using Student Data only for an authorized purpose and
            in furtherance of such legitimate educational interest;
          </$ListItem>
          <$ListItem>
            If a “Security Incident”, as defined in section 5.3 of the Along DPA
            , is primarily attributable to Gradient Learning, Gradient Learning
            shall subject to section 4.2 of the Along User Agreement, reimburse
            and indemnify School for any and all costs and expenses that the
            School incurs with: (a) providing notification to the parents of
            those students whose Student Data was compromised and regulatory
            agencies or other entities as required by law or contract; (b) audit
            costs, fines, and any other fees or damages imposed against the
            School as a result of the security breach; and (c) providing any
            other notifications or fulfilling any other requirements adopted by
            the Illinois State Board of Education or under other State or
            federal laws;
          </$ListItem>
          <$ListItem>
            The School shall notify Gradient Learning when the Student Data it
            has provided pursuant to the DPA is no longer needed for the
            School’s purpose(s) under the Agreement and this DPA. If any of the
            Student Data is no longer needed for purposes of the Agreement and
            this DPA, Gradient Learning will delete or transfer Student Data as
            set forth in section 4.6 of the DPA. Gradient Learning will comply
            with School’s request and delete or transfer the Student Data within
            a reasonable time period, not to exceed 30 days after verifying the
            written request, and according to a schedule and procedure as
            Gradient Learning and the School may reasonably agree.
          </$ListItem>
          <$ListItem>
            Pursuant to SOPPA, School shall publish on its website a copy of the
            Along DPA, including this Appendix.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Kentucky</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by Kentucky
          Revised Statutes 365.734, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            As set forth in section 4.2 of the Along DPA, Gradient Learning
            shall not process Student Data other than providing, improving,
            developing, or maintaining the integrity of the Services to the
            School as authorized pursuant to the Agreement. The Along User
            Agreement and the Along Privacy Policy set forth the exclusive
            purposes for which the Student Data will be used by Gradient
            Learning;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.7 of the Along DPA and section 3 of the
            Along Privacy Policy, Gradient Learning shall not process Student
            Data to advertise or facilitate advertising or to create or correct
            an individual or household for any advertisement purpose, and shall
            not sell, disclose or otherwise process Student Data for any
            commercial purpose.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Nevada</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by Nevada Revised
          Statute 388.272, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Gradient Learning’s commitments to protect the privacy and security
            of Student Data are set forth in the Along DPA, including section
            5.1, and are outlined in the Along Privacy Policy; and
          </$ListItem>
          <$ListItem>
            Gradient Learning acknowledges it may face potential liability as a
            penalty for intentional or grossly negligent noncompliance with this
            Agreement, including termination of the Agreement and payment of
            monetary damages, subject to section 4.2 of the Along User
            Agreement, for any breach of the terms of this Agreement that cause
            actual harm to the School.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">New York</$SectionTitleBold>
        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by New York State
          Education Law § 2-d (“Section 2-d”), and Part 121 of the New York
          State Education Department (“NYSED”) regulations implementing Section
          2-d.
        </$Paragraph>
        <$Paragraph>
          New York’s Parents Bill of Rights for Data Privacy and Security is
          incorporated into the Agreement and Gradient Learning agrees and
          acknowledges that:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            A student’s personally identifiable information cannot be sold or
            released for any commercial purposes;
          </$ListItem>
          <$ListItem>
            Parents have the right to inspect and review the complete contents
            of their child’s education record that is shared with or collected
            by Gradient Learning, as set forth in section 2.2 of the Along DPA
            and section 4 of the Along Privacy Policy;
          </$ListItem>
          <$ListItem>
            Gradient Learning complies with all applicable state and federal
            laws that protect the confidentiality of personally identifiable
            information, and, as set forth in section 5.1 of the Along DPA and
            section 6 of the Along Privacy Policy, employs safeguards associated
            with industry standards and best practices, including but not
            limited to, encryption, firewalls, and password protection, when
            data is stored or transferred;
          </$ListItem>
          <$ListItem>
            A complete list of all student data elements collected by Gradient
            Learning is available for public review in section 3.1 of the Along
            Privacy Policy; and
          </$ListItem>
          <$ListItem>
            Parents have the right to have complaints about possible breaches of
            student data addressed. Gradient Learning will promptly address any
            such complaints directed to&nbsp;
            <$PrivacyAtAlongEmailLink />.
          </$ListItem>
          <$ListItem>
            The Along User Agreement and the Along Privacy Policy set forth the
            exclusive purposes for which the student data or teacher or
            principal data will be used by Gradient Learning;
          </$ListItem>
          <$ListItem>
            As set forth in section 2.5 of the Along DPA and section 3.4 of the
            Along Privacy Policy, Gradient Learning may disclose the student
            data or teacher or principal data to subcontractors, or other
            authorized persons or entities (“Service Providers”) in order to
            perform its duties under the User Agreement. Gradient Learning shall
            enter into written agreements with all Service Providers and shall
            be responsible for any actions of Service Providers that would be a
            breach of this document.
          </$ListItem>
          <$ListItem>
            Section 4.6 of the Along DPA and section 7 of the Along Privacy
            Policy sets forth the time period, not to exceed 60 days, and
            process by which Gradient Learning will either delete or transfer
            personally identifiable information upon the expiration of the
            contract or when requested to do so by notification from the
            contracting party;
          </$ListItem>
          <$ListItem>
            A parent, student, eligible student, teacher or principal may
            correct inaccurate student data or teacher or principal data that is
            collected by Gradient Learning as set forth in section 2.2 of the
            Along DPA and section 4 of the Along Privacy Policy;
          </$ListItem>
          <$ListItem>
            Where required by applicable law, all student data or teacher or
            principal data will be stored within the United States and protected
            by employing administrative, physical, and technical safeguards
            designed to protect it from unauthorized access, disclosure, and use
            or acquisition by an unauthorized person, including when
            transmitting and storing such information.
          </$ListItem>
          <$ListItem>
            The data will be protected using encryption while in transit and at
            rest as further detailed in section 5.1 of the Along DPA.
          </$ListItem>
          <$ListItem>
            For purposes of compliance with NYSED regulation Part 121.6
            implementing Section 2-d, details of Gradient Learning’s data
            privacy and security plan can be found in our&nbsp;
            <$SecurityWhitepaperLink />.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">North Carolina</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by North Carolina
          General Statutes § 115C-402.5(b)(6), and includes the following
          statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Gradient Learning’s commitments to safeguard the privacy and
            security of Student Data are set forth in the Along DPA, including
            section 5.1, and are outlined in the Along Privacy Policy; and
          </$ListItem>
          <$ListItem>
            Gradient Learning acknowledges it may face potential liability as a
            penalty for intentional or grossly negligent noncompliance with this
            Agreement, including termination of the Agreement and payment of
            monetary damages, subject to section 4.2 of the Along User
            Agreement, for any breach of the terms of this Agreement that cause
            actual harm to the School.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Oklahoma</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by Oklahoma
          statute § 70-3-168, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Gradient Learning’s commitments to safeguard the privacy and
            security of Student Data are set forth in the Along DPA, including
            section 5.1, and are outlined in the Along Privacy Policy; and
          </$ListItem>
          <$ListItem>
            Gradient Learning acknowledges it may face potential liability as a
            penalty for intentional or grossly negligent noncompliance with this
            Agreement, including termination of the Agreement and payment of
            monetary damages, subject to section 4.2 of the Along User
            Agreement, for any breach of the terms of this Agreement that cause
            actual harm to the School.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">Rhode Island</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by Rhode Island HB
          7124, as codified by General Laws § 16-104-1, and includes the
          following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            As set forth in section 4.2 of the Along DPA, Gradient Learning
            shall process data of a student enrolled in kindergarten through
            twelfth (12th) grade (“Student Data”) for the sole purpose of
            providing the Services to the School as authorized pursuant to the
            Agreement. The Along User Agreement and the Along Privacy Policy set
            forth the exclusive purposes for which the Student Data will be used
            by Gradient Learning;
          </$ListItem>
          <$ListItem>
            As set forth in section 4.7 of the Along DPA and section 3 of the
            Along Privacy Policy, Gradient Learning shall not process Student
            Data for any commercial purposes, including, but not limited to,
            advertising purposes that benefit Gradient Learning.
          </$ListItem>
        </$UnorderedList>

        <$SectionTitleBold size="h1">West Virginia</$SectionTitleBold>

        <$Paragraph>
          The Along User Agreement, incorporating the Along DPA and this
          Appendix constitutes the written agreement mandated by West Virginia
          Code § 18-2-5h, and includes the following statements:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            Gradient Learning’s commitments to safeguard the privacy and
            security of Student Data are set forth in the Along DPA, including
            section 5.1, and are outlined in the Along Privacy Policy; and
          </$ListItem>
          <$ListItem>
            Gradient Learning acknowledges it may face potential liability as a
            penalty for intentional or grossly negligent noncompliance with this
            Agreement, including termination of the Agreement and payment of
            monetary damages, subject to section 4.2 of the Along User
            Agreement, for any breach of the terms of this Agreement that cause
            actual harm to the School.
          </$ListItem>
        </$UnorderedList>
      </$TextContainer>
    </PolicyLayout>
  );
}
